'use client'

import Image from 'next/image'
import Link from 'next/link'
import FullBleedSection from '@/components/FullBleedSection'
import { motion } from 'framer-motion'
import HeizungFAQ from '@/components/HeizungFAQ'
import FeatureCards from '@/components/FeatureCards'
import AIOptimizedSchema from '@/components/AIOptimizedSchema'

// AI-optimized schema data for Heizung service
const heizungSchemaData = {
  name: 'Volks-Heizung',
  description:
    'Effiziente Infrarotheizung mit Top-Preis-Leistungs-Verhältnis. Deutsche Qualität, einfache Installation und optimale Wärmeverteilung.',
  url: 'https://godeutschland.de/heizung',
  price: '305',
  category: 'Heizsystem',
  features: [
    'Infrarot-Technologie',
    'Deutsche Produktion',
    'Strahlungswärme ohne Luftverwirbelungen',
    'Schnelle Installation',
    'Gleichmäßige Wärmeverteilung',
    'Angenehmes Raumklima',
    'Energieeffizient',
    'Wartungsfrei',
  ],
  benefits: [
    'Günstige Wärme',
    'Heizkosten senken',
    'Keine staubige Heizungsluft',
    'Sofortige Wärme',
    'Platzsparend',
    'Umweltfreundlich',
  ],
  breadcrumbs: [
    { name: 'Home', url: 'https://godeutschland.de' },
    { name: 'Heizung', url: 'https://godeutschland.de/heizung' },
  ],
  faqs: [
    {
      question: 'Wie funktioniert eine Infrarotheizung?',
      answer:
        'Infrarotheizungen erzeugen Strahlungswärme, die direkt Personen und Gegenstände erwärmt, ohne die Luft zu erhitzen. Das sorgt für angenehme Wärme ohne Luftverwirbelungen.',
    },
    {
      question: 'Was kostet eine Infrarotplatte?',
      answer:
        'Unsere Infrarotplatten beginnen ab 305 € je Platte, inklusive Planung, Beratung, Platzierung und Versand.',
    },
    {
      question: 'Wie wird eine Infrarotheizung installiert?',
      answer:
        'Die Installation ist schnell und einfach. Die Platten werden an der Wand oder Decke montiert und an das Stromnetz angeschlossen.',
    },
    {
      question: 'Sind Infrarotheizungen energieeffizient?',
      answer:
        'Ja, durch die direkte Strahlungswärme und bedarfsgerechte Steuerung können Heizkosten erheblich gesenkt werden.',
    },
  ],
}

export default function HeizungPage() {
  return (
    <>
      {/* AI-Optimized Schema Markup for Heizung Service */}
      <AIOptimizedSchema
        type="service"
        serviceData={{
          name: 'Volks-Heizung',
          description: 'Effiziente Infrarotheizung mit Top-Preis-Leistungs-Verhältnis',
          price: '305',
          category: 'Heizsystem',
          features: ['Infrarot-Technologie', 'Deutsche Produktion', 'Energieeffizient'],
        }}
      />

      <div>
        {/* Hero Image Section */}
        <div className="relative min-h-screen"></div>
        <div className="absolute inset-0 -z-10">
          <Image
            src="/images/Heizung-hero.webp"
            alt="Photovoltaik Anlage"
            quality={100}
            fill
            priority
            className="object-cover"
            sizes="100vw"
          />
          <div className="absolute inset-0 bg-black/20" />
        </div>

        {/* Hero Text */}
        <div className="absolute inset-0 z-10">
          <div className="relative z-10 mx-auto max-w-7xl">
            <div className="flex min-h-screen flex-col px-4 pt-32 sm:block sm:px-6 md:px-8 lg:px-10">
              {/* Hero Content */}
              <div className="flex flex-grow flex-col justify-center pb-8 text-center sm:absolute sm:inset-0 sm:mb-60 md:pl-0 md:text-left">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8 }}
                >
                  <h1 className="mb-6 text-4xl font-bold text-white md:text-5xl lg:text-6xl">
                    Die Volks-<span className="text-[#ff4500]">Heizung</span>
                  </h1>
                  <p className="mb-4 text-xl text-white md:text-2xl">
                    Effiziente Infrarotheizung mit Top-Preis-Leistungs-Verhältnis
                  </p>
                  <p className="mb-8 text-lg text-white">
                    Wir bieten Infrarotheizungen für jeden Raum in jeglicher Form aus deutscher
                    Produktion.
                    <br />
                    Egal ob schlicht und günstig, oder extravagant – wir finden die optimale Lösung!
                  </p>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                    className="mb-8 md:mb-0"
                  >
                    <Link
                      href="/termin"
                      className="inline-block rounded-lg bg-[#ff4500] px-8 py-4 font-bold text-white transition-all hover:scale-105 hover:bg-[#e03e00]"
                    >
                      Jetzt Angebot anfordern
                    </Link>
                  </motion.div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>

        {/* Product Features Grid - Bento Box Highlights */}
        <FullBleedSection className="bg-white py-12 dark:bg-gray-900">
          <div className="mx-auto max-w-7xl">
            {/* First row - 3 boxes with different widths */}
            <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-12">
              {/* Left Box - Heating Features */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="col-span-1 flex flex-col items-center justify-center rounded-md bg-[#212121] p-8 text-center text-white md:col-span-3"
                style={{ paddingTop: '5%', paddingBottom: '5%' }}
              >
                <div className="">
                  <h2 className="mb-2 text-4xl font-bold">
                    <span>Günstige Wärme</span>
                  </h2>
                  <p>Durch Infrarot können Heizkosten enorm gesenkt werden</p>
                </div>
              </motion.div>

              {/* Middle Box - Main Features */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="col-span-1 flex flex-col rounded-md bg-[#e9e9e6] p-6 text-[#21262b] md:col-span-6"
              >
                <div className="grid grid-cols-1 gap-4 text-center md:grid-cols-2 md:text-left">
                  <div>
                    <h3 className="text-lg font-medium">Innovative</h3>
                    <h2 className="mt-2 text-4xl font-bold">
                      Infrarot-
                      <br />
                      Technologie
                    </h2>
                    <p className="mt-4">
                      Angenehme Strahlungswärme ohne Luftverwirbelungen, kombiniert mit schneller
                      und einfacher Installation
                    </p>
                  </div>
                  <div className="flex items-center justify-center md:justify-end">
                    <div className="relative h-48 w-48">
                      <Image
                        src="/images/heating-system.webp"
                        alt="Volks-Heizung"
                        fill
                        className="object-contain"
                      />
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Right Box - Price */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="col-span-1 flex flex-col items-center justify-center rounded-md bg-[#e9e9e6] p-8 text-center text-[#21262b] md:col-span-3"
                style={{ paddingTop: '5%' }}
              >
                <h2 className="text-4xl font-bold">
                  <span>Ab 305 € je Infrarotplatte </span>
                </h2>
                <p className="mt-4">Inkl. Planung, Beratung, Platzierung und Versand</p>
              </motion.div>
            </div>

            {/* Second row */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-12">
              {/* Left column */}
              <div className="col-span-1 md:col-span-6">
                {/* Top Box */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  className="mb-4 rounded-md bg-white p-12 text-center"
                >
                  <h2 className="text-4xl font-bold">Gleichmäßige Wärmeverteilung</h2>
                </motion.div>

                {/* Bottom Box */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                  className="relative overflow-hidden rounded-md p-10"
                >
                  {/* Background Image */}
                  <div className="absolute inset-0 z-0">
                    <video
                      src="/videos/solar-sun-1.webm"
                      className="h-full w-full object-cover object-center"
                      autoPlay
                      loop
                      muted
                      playsInline // Add this to prevent fullscreen on iOS
                      preload="auto" // Add this for better loading
                      style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                      }}
                    />
                    <div className="absolute inset-0 bg-black/30" />
                  </div>

                  {/* Content */}
                  <div className="relative z-10 flex h-full items-center justify-center">
                    <div className="text-center">
                      <h2 className="text-4xl font-bold text-white">Angenehmes Raumklima</h2>
                      <p className="text-xl text-white">
                        durch die Vermeidung staubiger Heizungsluft
                      </p>
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Right column - 2 boxes */}
              <div className="col-span-1 md:col-span-6">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {/* Left Box */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.5 }}
                    className="flex flex-col items-center justify-center rounded-md bg-[#ff4500] p-8 text-center text-white"
                    style={{ paddingTop: '5%', paddingBottom: '5%' }}
                  >
                    <h2 className="mb-2 text-5xl font-bold">Keine Kosten</h2>
                    <p className="text-center">durch Verschleiß, Wartung, Rohrbruch</p>
                  </motion.div>

                  {/* Right Box */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.6 }}
                    className="relative flex flex-col items-center justify-center overflow-hidden rounded-md p-21 text-center text-white"
                    style={{
                      paddingTop: '16%',
                    }}
                  >
                    <div className="absolute inset-0 z-10 bg-[#21262b] opacity-20"></div>
                    <div className="absolute inset-0 z-0">
                      <Image
                        src="/images/infrared-heater.webp"
                        alt="Infrarotheizung Installation"
                        fill
                        className="object-cover"
                      />
                    </div>
                    <div className="relative z-20 text-center">
                      <h2 className="mb-2 text-4xl font-bold">
                        Attraktives Preis-Leistungs-Verhältnis
                      </h2>
                    </div>
                  </motion.div>
                </div>
              </div>
            </div>
          </div>
        </FullBleedSection>

        {/* Installation Section */}
        <FullBleedSection className="bg-gray-100 py-20 dark:bg-gray-900/50">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <span className="text-lg font-semibold tracking-wider text-[#ff4500] uppercase">
              Kostengünstige Alternative
            </span>
            <h2 className="mt-6 text-center text-4xl font-bold text-gray-900 sm:text-5xl">
              Geringe Anschaffungskosten und <span className="text-[#ff4500]">kaum Wartung</span>
              <br />
              dank Infrarotheizung
            </h2>

            <p className="mx-auto mt-6 max-w-4xl text-center text-xl text-gray-600">
              Die Infrarotheizung erhält zurecht mehr und mehr Zulauf. Denn sie ist nicht nur eine
              attraktive Alternative zur klassischen Öl- oder Gasheizung, sondern auch zur
              Wärmepumpe! Sie verursacht wesentlich weniger Kosten, sieht sehr gut aus und liefert
              ein Wohlfühl-Raumklima.
            </p>
          </motion.div>
        </FullBleedSection>

        <div className="mt-10 flex justify-center">
          <Link
            href="/termin"
            className="rounded-lg bg-[#ff4500] px-8 py-4 font-bold text-white transition-colors hover:bg-[#e03e00]"
          >
            Jetzt Angebot anfordern
          </Link>
        </div>

        {/* Infrared Heating Types Section */}
        <FullBleedSection className="bg-white py-10 dark:bg-gray-900">
          <div className="mx-auto max-w-7xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="mb-12 text-center"
            >
              <h2 className="text-3xl font-bold md:text-4xl">
                Schöner als die <span className="text-[#ff4500]">Wärmepumpe</span>
              </h2>
              <p className="mx-auto mt-6 max-w-4xl text-xl text-gray-600">
                Welche Arten von Infrarotheizungen gibt es?
              </p>
              <p className="mx-auto mt-4 max-w-4xl text-lg text-gray-600">
                Unsere Infrarotheizungen werden höchsten ästhetischen Ansprüchen gerecht und passen
                sich in der Form an die Wünsche und Gegebenheiten an. Es folgt ein Überblick von
                Arten, die zur Verfügung stehen. Die Vielfalt einzelner Produkte ist jedoch so groß,
                dass wir Ihnen in jedem Fall dazu raten, ein kostenfreies Beratungsgespräch in
                Anspruch zu nehmen und Ihren Fall zu besprechen:
              </p>
            </motion.div>

            <FeatureCards
              className="py-0"
              title=""
              features={[
                {
                  image: '/images/wall-heating.webp',
                  imageAlt: 'Infrarot-Flächenheizungen',
                  title: 'Infrarot-Flächenheizungen',
                  description:
                    'Alles in Ihrem Raum kann zur Heizfläche werden, wenn es mit unserer Infrarot-Heizfolie beschichtet wird. So sind in Kreativität und Ästhetik keine Grenzen gesetzt.',
                },
                {
                  image: '/images/heating-panel.webp',
                  imageAlt: 'Infrarot-Heizpaneele',
                  title: 'Infrarot-Heizpaneele',
                  description:
                    'Die vielleicht kostengünstigste Alternative im Heizungssektor. Denn Infrarot-Heizpaneele lassen sich sehr leicht anbringen und nachrüsten. Zudem bieten sie einen dekorativen Nutzen.',
                },
                {
                  image: '/images/radiant-heaters.webp',
                  imageAlt: 'Infrarot-Heizstrahler',
                  title: 'Infrarot-Heizstrahler',
                  description:
                    'Egal ob privater Wintergarten, Terrasse oder gewerbliches Lager, Außenbereich, Tierzucht: Der Infrarot-Heizstrahler schafft zuverlässig Wärme und Wohlfühlklima.',
                },
              ]}
            />
          </div>
        </FullBleedSection>

        <FullBleedSection className="bg-gray-50 py-20 dark:bg-gray-800">
          <div className="mx-auto max-w-7xl">
            <div className="grid gap-8 md:grid-cols-2">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <h2 className="mb-6 text-center text-3xl font-bold md:text-left md:text-4xl">
                  Weitere Vorteile der <span className="text-[#ff4500]">Infrarotheizung</span>
                </h2>
                <p className="mb-8 text-center text-lg text-gray-600 md:text-left dark:text-gray-300">
                  Die bekanntesten Vorteile der Infrarotheizung liegen in den geringen
                  Anschaffungskosten bei großer Leistung.
                </p>
                <p className="mb-8 text-center text-lg text-gray-600 md:text-left dark:text-gray-300">
                  Infrarotheizungen können aber noch mehr:
                </p>
                <ul className="mb-8 space-y-4">
                  <li className="flex items-center text-gray-600 dark:text-gray-300">
                    <span className="mr-2 h-5 w-5 text-[#ff4500]">✓</span>
                    Unsere Infrarotheizung werden in Deutschland produziert
                  </li>
                  <li className="flex items-center text-gray-600 dark:text-gray-300">
                    <span className="mr-2 h-5 w-5 text-[#ff4500]">✓</span>
                    Gleichmäßige Wärmeverteilung
                  </li>
                  <li className="flex items-center text-gray-600 dark:text-gray-300">
                    <span className="mr-2 h-5 w-5 text-[#ff4500]">✓</span>
                    Allergiker-freundlich
                  </li>
                  <li className="flex items-center text-gray-600 dark:text-gray-300">
                    <span className="mr-2 h-5 w-5 text-[#ff4500]">✓</span>
                    Wirkt Schimmelpilzbildung entgegen
                  </li>
                  <li className="flex items-center text-gray-600 dark:text-gray-300">
                    <span className="mr-2 h-5 w-5 text-[#ff4500]">✓</span>
                    Kein Aufwirbeln von Staub, Viren und Bakterien
                  </li>
                  <li className="flex items-center text-gray-600 dark:text-gray-300">
                    <span className="mr-2 h-5 w-5 text-[#ff4500]">✓</span>
                    Umweltfreundlich
                  </li>
                  <li className="flex items-center text-gray-600 dark:text-gray-300">
                    <span className="mr-2 h-5 w-5 text-[#ff4500]">✓</span>
                    Kein Verschleiß, daher kein Wartungsaufwand und keine Folgekosten
                  </li>
                  <li className="flex items-center text-gray-600 dark:text-gray-300">
                    <span className="mr-2 h-5 w-5 text-[#ff4500]">✓</span>
                    Lüften ohne große Wärmeverluste
                  </li>
                  <li className="flex items-center text-gray-600 dark:text-gray-300">
                    <span className="mr-2 h-5 w-5 text-[#ff4500]">✓</span>
                    Kein Schornsteinfeger nötig und kein Rohrbruch möglich
                  </li>
                  <li className="flex items-center text-gray-600 dark:text-gray-300">
                    <span className="mr-2 h-5 w-5 text-[#ff4500]">✓</span>
                    Einfache Installation
                  </li>
                  <li className="flex items-center text-gray-600 dark:text-gray-300">
                    <span className="mr-2 h-5 w-5 text-[#ff4500]">✓</span>
                    Präzise Temperatur-Regelung, bequem durch Fernsteuerung
                  </li>
                </ul>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                className="relative min-h-[400px]"
              >
                <div className="relative h-full w-full overflow-hidden rounded-lg">
                  <Image
                    src="/images/haus.webp"
                    alt="Infrarotheizung im Einsatz"
                    fill
                    className="object-cover"
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </FullBleedSection>

        <FullBleedSection className="bg-white py-10 dark:bg-gray-900">
          <div className="mx-auto max-w-7xl">
            <FeatureCards
              className="py-0"
              title="Sie profitieren von unserem Rundum-Service"
              features={[
                {
                  image: '/images/service-benefits.webp',
                  imageAlt: 'Infrarot-Flächenheizungen',
                  title: 'Infrarot-Flächenheizungen',
                  description:
                    'Alles in Ihrem Raum kann zur Heizfläche werden, wenn es mit unserer Infrarot-Heizfolie beschichtet wird. So sind in Kreativität und Ästhetik keine Grenzen gesetzt.',
                },
                {
                  image: '/images/heating-service-2.webp',
                  imageAlt: 'Infrarot-Heizpaneele',
                  title: 'Infrarot-Heizpaneele',
                  description:
                    'Die vielleicht kostengünstigste Alternative im Heizungssektor. Denn Infrarot-Heizpaneele lassen sich sehr leicht anbringen und nachrüsten. Zudem bieten sie einen dekorativen Nutzen.',
                },
                {
                  image: '/images/install-service.webp',
                  imageAlt: 'Infrarot-Heizstrahler',
                  title: 'Infrarot-Heizstrahler',
                  description:
                    'Egal ob privater Wintergarten, Terrasse oder gewerbliches Lager, Außenbereich, Tierzucht: Der Infrarot-Heizstrahler schafft zuverlässig Wärme und Wohlfühlklima.',
                },
              ]}
            />
          </div>
        </FullBleedSection>

        {/* How Infrared Heating Works Section */}
        <FullBleedSection className="bg-gray-50 py-16 dark:bg-gray-800">
          <div className="mx-auto max-w-7xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="mb-12 text-center"
            >
              <h2 className="mb-8 text-3xl font-bold md:text-4xl">
                Wie funktioniert eine <span className="text-[#ff4500]">Infrarotheizung</span>?
              </h2>
              <p className="mx-auto mb-8 max-w-4xl text-lg text-gray-700 dark:text-gray-300">
                Anders als herkömmliche Konvektionsheizungen, die primär die Luft erhitzen, arbeiten
                Infrarotheizungen so: Sie geben eine sanfte Strahlung ab, die direkt auf feste
                Körper im Raum wirkt – seien es Ihre Möbel, die Wände, der Boden oder Sie selbst.
              </p>
              <p className="mx-auto mb-8 max-w-4xl text-lg text-gray-700 dark:text-gray-300">
                Diese Oberflächen absorbieren die Infrarotstrahlen und speichern die Wärme, um sie
                dann allmählich und gleichmäßig an die Umgebung abzugeben. Das Ergebnis ist eine
                tief empfundene, behagliche Wärme, die oft mit dem angenehmen Gefühl natürlicher
                Sonnenstrahlen verglichen wird. Sogar Oberflächen, die sich sonst kühl anfühlen
                würden, wie Fliesenböden oder Metallarmaturen, strahlen dann eine angenehme
                Temperatur aus.
              </p>
            </motion.div>

            <div className="mb-16 grid gap-12 md:grid-cols-2">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                className="relative mb-8 flex h-72 flex-col items-center justify-center md:h-96"
              >
                <Image
                  src="/images/Schaubild_Strahlungswärme.webp"
                  alt="Strahlungswärme einer Infrarotheizung"
                  fill
                  sizes="(max-width: 768px) 100vw, 50vw"
                  className="rounded-lg"
                />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                className="flex flex-col justify-center"
              >
                <h3 className="mb-4 text-2xl font-bold md:text-3xl">
                  Der Vorteil durch <span className="text-[#ff4500]">Strahlungswärme</span>
                </h3>
                <p className="mb-6 text-lg text-gray-700 dark:text-gray-300">
                  Bei der herkömmlichen Konvektionswärme wird die Wärme an die Luft abgegeben und
                  zirkuliert im Raum. Sie steigt von unten nach oben und sorgt für unterschiedliche
                  Temperaturzonen. Weitere Nachteile: Die Wände bleiben kalt und es kann sich
                  Schwitzwasser bilden.
                </p>
                <p className="text-lg text-gray-700 dark:text-gray-300">
                  Bei der Strahlungswärme zirkuliert nichts. Die Wärme wird gleichmäßig im Raum
                  verteilt. Dabei wird auch nichts aufgewirbelt, wie Staub, Bakterien, Viren.
                </p>
              </motion.div>
            </div>

            <div className="grid gap-12 md:grid-cols-2">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                className="flex flex-col justify-center md:order-2"
              >
                <h3 className="mb-4 text-2xl font-bold md:text-3xl">
                  Konventionelle <span className="text-[#ff4500]">Heizung</span>
                </h3>
                <p className="mb-6 text-lg text-gray-700 dark:text-gray-300">
                  Herkömmliche Heizsysteme erwärmen hauptsächlich die Luft, was zu einer
                  ungleichmäßigen Wärmeverteilung führt. Die warme Luft steigt nach oben, während es
                  am Boden kühl bleibt. Dies kann zu Zugluft, kalten Ecken und höheren Energiekosten
                  führen.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                className="relative mb-8 flex h-72 flex-col items-center justify-center rounded-4xl md:order-1 md:mb-0 md:h-96"
              >
                <Image
                  src="/images/Schaubild_Konvektion.webp"
                  alt="Konvektionswärme einer herkömmlichen Heizung"
                  fill
                  sizes="(max-width: 768px) 100vw, 50vw"
                  className="rounded-lg"
                />
              </motion.div>
            </div>
          </div>
        </FullBleedSection>

        {/* FAQ Section */}
        <HeizungFAQ />
      </div>
    </>
  )
}
